import { events } from '../events';
import { checkCompatibility } from '../utils/check-compatibility.fn';
import { BaseComponent } from './base.component';
import { ComponentRegistry } from './component-registry';
import { UnsupportedBrowserError } from './models/unsupported-browser.error';
import { Module } from './module';
import { Constructor } from './types/constructor.type';

export abstract class Application {
  private modules: Module[] = [];

  constructor(
    protected appName: string,
    protected options: {
      declarations?: Constructor<BaseComponent<any>>[];
      modules?: Constructor<Module>[];
      tracesSampler?: (samplingContext: {
        transactionContext?: { op?: string };
      }) => number | boolean;
    }
  ) {}

  protected abstract onInit(): Promise<void>;

  async start() {
    try {
      this.registerTelemetry();
      this.compatibilityCheck();
      this.declareComponents();
      this.registerModules();
      ComponentRegistry.bootstrap();

      await this.onInit();

      events.on('error', ({ error, errorPayload }) => {
        console.error(error, errorPayload);
      });

      events.on('warn', ({ message: err }) =>
        console.warn(typeof err === 'string' ? err : err.message)
      );
    } catch (e) {
      console.error(e);
    }
  }

  protected registerTelemetry() {
    console.info('No telemetry');
  }

  protected compatibilityCheck() {
    if (!checkCompatibility()) {
      throw new UnsupportedBrowserError();
    }
  }

  protected declareComponents() {
    this.options?.declarations?.forEach((component) =>
      ComponentRegistry.declare(component)
    );
  }

  protected tracesSampler(samplingContext: {
    transactionContext?: { op?: string };
  }): number | boolean {
    if (typeof this.options?.tracesSampler === 'function') {
      return this.options.tracesSampler(samplingContext);
    }

    return 0.1;
  }

  protected async registerModules() {
    const modules = await Promise.all(
      this.options?.modules
        ?.map((m) => new m())
        .map(async (m) => {
          await m.register();
          return m;
        }) ?? []
    );

    this.modules.push(...modules);
  }
}
